import React, { useState, useEffect, useRef } from 'react';
import { countries, formatPhone, getCountryByDDI, getCountryByCode } from '../utils/countries';
import './PhoneInput.css';

const PhoneInput = ({ value = '', onChange, placeholder, required = false, id, countryCode: initialCountryCode = 'BR' }) => {
  const [phone, setPhone] = useState('');
  const [selectedCountry, setSelectedCountry] = useState(getCountryByCode(initialCountryCode));
  const [showCountryList, setShowCountryList] = useState(false);
  const [searchCountry, setSearchCountry] = useState('');
  const wrapperRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    // Se value já vem com DDI, extrair e formatar
    if (value) {
      const ddiMatch = value.match(/^(\+\d{1,4})\s*(.+)$/);
      if (ddiMatch) {
        const [, ddi, number] = ddiMatch;
        const country = getCountryByDDI(ddi);
        if (country) {
          setSelectedCountry(country);
          setPhone(formatPhone(number, country.code));
        } else {
          setPhone(formatPhone(value, selectedCountry.code));
        }
      } else {
        // Se não tem DDI, assumir que é só o número
        setPhone(formatPhone(value, selectedCountry.code));
      }
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowCountryList(false);
        setSearchCountry('');
      }
    };

    if (showCountryList) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCountryList]);

  const handlePhoneChange = (e) => {
    const inputValue = e.target.value;
    // Permite apenas números
    const numbersOnly = inputValue.replace(/\D/g, '');
    const formatted = formatPhone(numbersOnly, selectedCountry.code);

    setPhone(formatted);

    // Callback com valor completo (DDI + número)
    if (onChange) {
      const fullValue = `${selectedCountry.ddi} ${numbersOnly}`;
      onChange({ target: { value: fullValue } });
    }
  };

  const handleCountrySelect = (country) => {
    setSelectedCountry(country);
    setShowCountryList(false);
    setSearchCountry('');

    // Reformatar número atual com novo formato de país
    const numbersOnly = phone.replace(/\D/g, '');
    const formatted = formatPhone(numbersOnly, country.code);
    setPhone(formatted);

    // Callback com valor completo
    if (onChange) {
      const fullValue = `${country.ddi} ${numbersOnly}`;
      onChange({ target: { value: fullValue } });
    }
  };

  const filteredCountries = countries.filter(country =>
    country.name.toLowerCase().includes(searchCountry.toLowerCase()) ||
    country.ddi.includes(searchCountry) ||
    country.code.toLowerCase().includes(searchCountry.toLowerCase())
  );

  return (
    <div className="phone-input-wrapper" ref={wrapperRef}>
      <div className="phone-input-container">
        <button
          type="button"
          className="phone-country-selector"
          onClick={() => setShowCountryList(!showCountryList)}
        >
          <span className="phone-flag">{selectedCountry.flag}</span>
          <span className="phone-ddi">{selectedCountry.ddi}</span>
          <span className="phone-chevron">▼</span>
        </button>

        {showCountryList && (
          <div className="phone-country-dropdown" ref={dropdownRef}>
            <div className="phone-country-search">
              <input
                type="text"
                placeholder="Buscar país..."
                value={searchCountry}
                onChange={(e) => setSearchCountry(e.target.value)}
                autoFocus
              />
            </div>
            <div className="phone-country-list">
              {filteredCountries.map((country) => (
                <button
                  key={country.code}
                  type="button"
                  className={`phone-country-item ${selectedCountry.code === country.code ? 'active' : ''}`}
                  onClick={() => handleCountrySelect(country)}
                >
                  <span className="phone-flag">{country.flag}</span>
                  <span className="phone-country-name">{country.name}</span>
                  <span className="phone-ddi">{country.ddi}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        <input
          id={id}
          type="tel"
          className="phone-number-input"
          value={phone}
          onChange={handlePhoneChange}
          placeholder={placeholder || 'Número do telefone'}
          required={required}
          maxLength={20}
        />
      </div>
    </div>
  );
};

export default PhoneInput;
